import { LoadingButton } from '@mui/lab';
import { Box, Card, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { radius } from '~/styles/tokens/shape';
import { useContractExitGuardContext } from '../contractExitGuard';

type ContractStepCardProps = {
  title: string;
  stepLabel: string;
  children: ReactNode;
  actionsAppIdPrefix: string;
  loading?: boolean;
  primaryLabel?: string;
  onPrimary: () => void;
  onSaveExit?: () => void;
  primaryType?: 'button' | 'submit';
  primaryDisabled?: boolean;
};

export default function ContractStepCard({
  title,
  stepLabel,
  children,
  actionsAppIdPrefix,
  loading = false,
  primaryLabel = 'Próximo passo',
  onPrimary,
  onSaveExit,
  primaryType = 'submit',
  primaryDisabled = false
}: ContractStepCardProps) {
  const exitGuard = useContractExitGuardContext();
  // «Salvar e sair» sai do fluxo sem finalizar: dentro do shell passa pelo aviso (antes caía no `onPrimary` vazio).
  const handleSaveExit = onSaveExit ?? (exitGuard ? () => exitGuard.requestExit() : onPrimary);

  return (
    <Card
      data-app-id={`${actionsAppIdPrefix}-card`}
      elevation={0}
      sx={{
        borderRadius: `${radius.large}px`,
        boxShadow: '0px 1px 1px rgba(0,0,0,0.3), 0px 2px 3px rgba(0,0,0,0.15)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 3,
        width: '100%'
      }}
    >
      <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between' }}>
        <Typography variant="h4">{title}</Typography>
        <Box
          sx={(theme) => ({
            bgcolor: theme.customColors.backgroundTertiary,
            borderRadius: `${radius.medium}px`,
            px: 2,
            py: 0.5
          })}
        >
          <Typography variant="h5">{stepLabel}</Typography>
        </Box>
      </Box>

      {children}

      <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', pt: 1 }}>
        <LoadingButton
          data-app-id={`${actionsAppIdPrefix}-save-exit`}
          data-testid="contract-save-exit"
          disabled={loading}
          onClick={handleSaveExit}
          sx={(theme) => ({ color: theme.customColors.tertiaryMain, minWidth: 139 })}
          type="button"
          variant="text"
        >
          Salvar e sair
        </LoadingButton>
        <LoadingButton
          data-app-id={`${actionsAppIdPrefix}-next`}
          data-testid="contract-next-step"
          disabled={primaryDisabled}
          loading={loading}
          onClick={primaryType === 'button' ? onPrimary : undefined}
          sx={{ borderRadius: `${radius.large}px`, minWidth: 139 }}
          type={primaryType}
          variant="contained"
        >
          {primaryLabel}
        </LoadingButton>
      </Box>
    </Card>
  );
}
